<template>
  <Dialog v-model="dialogVisible" title="分销申请审核" width="640">
    <div v-loading="formLoading">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="用户编号">{{ detail.id || '-' }}</el-descriptions-item>
        <el-descriptions-item label="昵称">{{ detail.nickname || '-' }}</el-descriptions-item>
        <el-descriptions-item label="头像">
          <el-avatar :src="detail.avatar" />
        </el-descriptions-item>
        <el-descriptions-item label="申请状态">
          <el-tag v-if="detail.applyStatus === 1" type="warning">待审核</el-tag>
          <el-tag v-else-if="detail.applyStatus === 2" type="success">已通过</el-tag>
          <el-tag v-else-if="detail.applyStatus === 3" type="danger">已驳回</el-tag>
          <el-tag v-else type="info">未申请</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="申请时间">
          {{ detail.applyTime ? formatDate(detail.applyTime as any) : '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="真实姓名">{{ detail.applyRealName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="身份证号">{{ detail.applyIdCard || '-' }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ detail.applyMobile || '-' }}</el-descriptions-item>
        <el-descriptions-item label="银行名称">{{ detail.applyBankName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="银行卡号" :span="2">
          {{ detail.applyBankCardNo || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="身份证正面">
          <el-image
            v-if="detail.applyIdCardFrontUrl"
            :src="detail.applyIdCardFrontUrl"
            :preview-src-list="previewList"
            :initial-index="0"
            fit="cover"
            class="id-img"
          />
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="身份证反面">
          <el-image
            v-if="detail.applyIdCardBackUrl"
            :src="detail.applyIdCardBackUrl"
            :preview-src-list="previewList"
            :initial-index="Math.max(0, previewList.indexOf(detail.applyIdCardBackUrl))"
            fit="cover"
            class="id-img"
          />
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="人脸照片" :span="2">
          <el-image
            v-if="detail.applyFaceUrl"
            :src="detail.applyFaceUrl"
            :preview-src-list="previewList"
            :initial-index="Math.max(0, previewList.indexOf(detail.applyFaceUrl))"
            fit="cover"
            class="id-img"
          />
          <span v-else>-</span>
        </el-descriptions-item>
      </el-descriptions>
      <el-form class="mt-16px" label-width="80px">
        <el-form-item label="审核备注">
          <el-input
            v-model="auditReason"
            :disabled="!canAudit"
            :rows="3"
            maxlength="200"
            placeholder="驳回时请填写原因；通过可选填"
            show-word-limit
            type="textarea"
          />
        </el-form-item>
      </el-form>
    </div>
    <template #footer>
      <el-button
        v-if="canAudit"
        :disabled="formLoading"
        type="success"
        @click="submit(true)"
      >
        通过
      </el-button>
      <el-button
        v-if="canAudit"
        :disabled="formLoading"
        type="danger"
        @click="submit(false)"
      >
        驳回
      </el-button>
      <el-button @click="dialogVisible = false">关闭</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as BrokerageUserApi from '@/api/mall/trade/brokerage/user'
import { formatDate } from '@/utils/formatTime'

defineOptions({ name: 'BrokerageUserAuditDialog' })

const message = useMessage()
const emit = defineEmits(['success'])

const dialogVisible = ref(false)
const formLoading = ref(false)
const auditReason = ref('')
const detail = ref<Partial<BrokerageUserApi.BrokerageUserVO>>({})

const canAudit = computed(() => detail.value.applyStatus === 1)

const previewList = computed(() =>
  [detail.value.applyIdCardFrontUrl, detail.value.applyIdCardBackUrl, detail.value.applyFaceUrl].filter(
    (u): u is string => !!u
  )
)

const open = async (row: BrokerageUserApi.BrokerageUserVO) => {
  dialogVisible.value = true
  formLoading.value = true
  auditReason.value = ''
  detail.value = { ...row }
  try {
    const data = await BrokerageUserApi.getBrokerageUser(row.id)
    if (data) {
      detail.value = { ...row, ...data }
    }
  } catch {
    /* 列表行材料兜底 */
  } finally {
    formLoading.value = false
  }
}
defineExpose({ open })

const submit = async (approved: boolean) => {
  if (!detail.value.id) return
  if (!approved && !auditReason.value.trim()) {
    message.warning('请填写驳回原因')
    return
  }
  try {
    await message.confirm(approved ? '确认通过该分销申请？' : '确认驳回该分销申请？')
  } catch {
    return
  }
  formLoading.value = true
  try {
    await BrokerageUserApi.auditBrokerageUserApply({
      id: detail.value.id,
      approved,
      auditReason: auditReason.value.trim() || undefined
    })
    message.success(approved ? '已通过' : '已驳回')
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}
</script>
<style scoped>
.id-img {
  width: 120px;
  height: 80px;
  border-radius: 4px;
}
</style>
